import { eventRegistry } from '../events/registry';
import ETL, { IETL } from './data_layer/etl';
import { IDataLayer } from '../../types';
import { getCookie } from '../utils';
import { fetchPerlgemRPC } from '../utils';

export class DataLayer {
  data: IDataLayer;
  etl: IETL = ETL;
  perlgemEnabled: boolean = false;

  constructor() {
    window.utag_data = window.utag_data || {};
    this.data = window.utag_data;
  }

  async load(
    pageData: IDataLayer = {},
    detectEvents: boolean = false,
  ): Promise<void> {
    let dataLayer: IDataLayer = {};

    if (Object.keys(pageData).length) {
      // BOF
      dataLayer = this.loadELCOPageData(pageData, detectEvents);
    } else {
      // TOF
      const ssrDataLayer = this.getSSRDataLayer();

      if (ssrDataLayer.__be_platform === 'bifrost') {
        dataLayer = { ...ssrDataLayer };
      } else {
        try {
          const perlgemDataLayer = await this.getPerlgemDataLayer();
          dataLayer = { ...perlgemDataLayer, ...ssrDataLayer };
          this.perlgemEnabled = true;
        } catch (error) {
          dataLayer = { ...ssrDataLayer };
        }
      }
    }

    this.enrichDataEvents(dataLayer);
    this.enrichDataProducts(dataLayer);
    this.update(dataLayer);
  }

  async getPerlgemDataLayer(): Promise<IDataLayer> {
    return (
      (await fetchPerlgemRPC({
        method: 'analytics.getDataLayer',
        params: [{}],
      })) || {}
    );
  }

  getSSRDataLayer(): IDataLayer {
    return window.page_data?.['analytics-datalayer'] ?? {};
  }

  loadELCOPageData(
    pageData: IDataLayer = {},
    detectEvents: boolean = false,
  ): IDataLayer {
    let dataLayer: IDataLayer = {};
    try {
      // generate page specific data layer
      dataLayer = this.etl.page();

      // generate data layer from keys of elco_page_data
      Object.keys(pageData).forEach((key) => {
        if (this.etl[key as keyof IETL]) {
          // only process order data if page_type is order
          if (key === 'order' && dataLayer?.page_type != 'order') return;
          const processedData = this.etl[key as keyof IETL](pageData[key]);
          dataLayer = { ...dataLayer, ...processedData };
        }
      });

      // this is for change events, like remove from cart.
      // TODO: move this somewhere more appropriate
      // TODO: figure out a better way to deal with this -- currently, we can't rely on the
      //       processEvents in update() because remove cart event requires both the old and the new datalayer
      if (detectEvents) {
        this.detectEvents(dataLayer);
        this.processEvents();
      }
    } catch (e) {
      // add error handling
    }

    return dataLayer;
  }

  enrichDataEvents(dataLayer: IDataLayer): void {
    dataLayer.datalayer_events = dataLayer.datalayer_events || {};
    dataLayer.datalayer_events_processed =
      dataLayer.datalayer_events_processed || {};

    const dleCookie = getCookie('datalayer_events');
    const cookiedEvents = dleCookie ? dleCookie.split(',') : [];

    for (let i = 0; i < cookiedEvents.length; i++) {
      if (dataLayer.datalayer_events[cookiedEvents[i]]) {
        continue;
      }
      dataLayer.datalayer_events[cookiedEvents[i]] = {};
    }
  }

  enrichDataProducts(dataLayer: IDataLayer): void {
    if (dataLayer.product_impression_id) {
      dataLayer.product_impression_list = new Array(
        dataLayer.product_impression_id.length,
      ).fill(document.location.pathname);
      dataLayer.product_impression_position =
        dataLayer.product_impression_id.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (_: any, i: number) => i + 1,
        );
      dataLayer.product_impression_brand = dataLayer.product_impression_id.map(
        () => dataLayer.brand,
      );
    }
    if (Array.isArray(dataLayer.cart_product_id)) {
      dataLayer.cart_product_brand = dataLayer.cart_product_id.map(
        () => dataLayer.brand,
      );
    }
    if (Array.isArray(dataLayer.product_id)) {
      dataLayer.product_brand = dataLayer.product_id.map(() => dataLayer.brand);
    }
  }

  reset(dataLayer: IDataLayer): void {
    window.utag_data = dataLayer;
    this.data = window.utag_data;
    this.processEvents();
  }

  update(dataLayer: IDataLayer): void {
    Object.assign(this.data, dataLayer);
    if (dataLayer?.datalayer_events) {
      this.processEvents();
    }
  }

  processEvents(): void {
    const events = this.data.datalayer_events || {};

    if (Object.entries(events).length) {
      Object.entries(events).forEach(([eventName, eventData]) => {
        eventRegistry[eventName]?.(eventName, eventData?.event_data);
      });

      this.clearEvents();
    }
  }

  detectEvents(dataLayer: IDataLayer): void {
    this.data.datalayer_events = this.data.datalayer_events || {};

    // Removed from cart
    if (
      dataLayer.cart_product_product_code?.length !==
      this.data?.cart_product_product_code?.length
    ) {
      const pageProductCodes = dataLayer.cart_product_product_code || [];
      const removedProductCode =
        this.data?.cart_product_product_code?.filter(
          (x: number) => !pageProductCodes.includes(x),
        ) || [];

      this.data.datalayer_events['removeFromCart'] = {
        event_data: { product_code: removedProductCode },
      };
    }
  }

  async clearEvents(): Promise<void> {
    if (getCookie('datalayer_events')) {
      document.cookie =
        'datalayer_events=0;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    } else if (this.perlgemEnabled) {
      // RPC to server to delete all session events
      await fetchPerlgemRPC({ method: 'analytics.deleteEvents' });
    }

    this.data.datalayer_events_processed = {
      ...(this.data.datalayer_events_processed || {}),
      ...this.data.datalayer_events,
    };
    this.data.datalayer_events = {};
  }

  formatProductEventLabel(eventData: IDataLayer = this.data): string {
    return eventData.product_name && eventData.product_id
      ? `${this.stripOutMarkup(eventData.product_name[0])} - ${eventData.product_id[0]}`
      : '';
  }

  stripOutMarkup(str: unknown = ''): string {
    return typeof str === 'string' ? str.replace(/(<([^>]+)>)/gi, '') : '';
  }
}
